/**
 * report-dedup — pure duplicate-report detection for the report.init handler.
 *
 * Lifted VERBATIM from the report.init branch of the ui-action switch in
 * useChat.ts. The agent's report-builder loop occasionally re-fires report.init
 * after the first build completes, and ChatBox.tsx ALSO has a report.init
 * handler for the non-streaming path. To avoid spawning a duplicate module we
 * dedup against TWO sources of truth (normalized title compare):
 *   1) the per-turn activeReportModules map (catches dupes inside one stream)
 *   2) the workspace modules query cache (catches dupes across the
 *      useChat / ChatBox handler split)
 *
 * The hook keeps the React Query cache read, the `continue`, and the
 * console.info side effect; this helper owns only the pure title-match math.
 *
 * Pinned by the report.init dedup cases in
 * apps/web/src/features/chat/hooks/useChat.ui-directive-char.test.ts.
 */

/** Minimal shape this helper needs; the call site's concrete report entry
 *  (which carries a typed `config` plus other fields) is assignable to it. */
export interface ActiveReportEntry {
  config: unknown
  [key: string]: unknown
}

/**
 * Return the id of an existing report whose normalized title matches
 * `reportTitle`, or undefined if none. Checks the per-turn active map first
 * (insertion order), then the cached workspace modules list (non-archived
 * `report` modules).
 */
export function findDuplicateReportId(
  reportTitle: string,
  activeReportModules: ReadonlyMap<string, ActiveReportEntry>,
  cachedModules: unknown[] | undefined,
): string | undefined {
  const titleNorm = reportTitle.trim().toLowerCase()
  let dupOfId: string | undefined
  for (const [id, report] of activeReportModules) {
    const existingTitle = (report.config as { title?: unknown })?.title
      ?? (report as { title?: unknown })?.title
    if (typeof existingTitle === 'string' && existingTitle.trim().toLowerCase() === titleNorm) {
      dupOfId = id
      break
    }
  }
  if (!dupOfId) {
    if (Array.isArray(cachedModules)) {
      const dupModule = cachedModules.find(
        (m: any) =>
          m?.type === 'report'
          && !m?.archived
          && !m?.archivedAt
          && typeof m?.label === 'string'
          && m.label.trim().toLowerCase() === titleNorm,
      ) as { id?: string } | undefined
      if (dupModule?.id) dupOfId = dupModule.id
    }
  }
  return dupOfId
}
